import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Product, ProductVariant } from '@wonderherbals/shared';
import { useUIStore } from './useUIStore';
import { supabase } from '@/lib/supabase';

export interface CartItem {
  productId: string;
  slug: string;
  name: string;
  image: string;
  price: number; // in paise
  compareAtPrice?: number; // in paise
  variant?: Pick<ProductVariant, 'id' | 'name' | 'sku'>;
  quantity: number;
  maxQuantity: number;
}

interface CartState {
  items: CartItem[];
  couponCode: string | null;
  couponDiscount: number; // in paise
  isCartOpen: boolean;
  /** Mirrors admin Settings → Shipping (checkout on the server is authoritative). */
  shippingRules: { freeShippingThreshold: number; shippingCost: number };

  // Computed
  itemCount: () => number;
  subtotal: () => number;
  discount: () => number;
  shipping: () => number;
  gst: () => number;
  total: () => number;

  // Actions
  addItem: (product: Product, variant?: ProductVariant, quantity?: number) => void;
  removeItem: (productId: string, variantId?: string) => void;
  updateQuantity: (productId: string, variantId: string | undefined, quantity: number) => void;
  incrementQuantity: (productId: string, variantId?: string) => void;
  decrementQuantity: (productId: string, variantId?: string) => void;
  clearCart: () => void;
  applyCoupon: (code: string, discount: number) => void;
  removeCoupon: () => void;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
}

// Defaults until the live settings load; checkout recomputes everything server-side.
const DEFAULT_SHIPPING_RULES = { freeShippingThreshold: 99900, shippingCost: 9900 }; // paise
const GST_RATE = 0.18;
/** Per-line cap enforced by checkout. */
const MAX_LINE_QUANTITY = 50;

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      couponCode: null,
      couponDiscount: 0,
      isCartOpen: false,
      shippingRules: DEFAULT_SHIPPING_RULES,

      itemCount: () => {
        return get().items.reduce((sum, item) => sum + item.quantity, 0);
      },

      subtotal: () => {
        return get().items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      },

      discount: () => {
        return get().couponDiscount;
      },

      shipping: () => {
        const subtotal = get().subtotal();
        const discount = get().discount();
        const afterDiscount = subtotal - discount;
        const { freeShippingThreshold, shippingCost } = get().shippingRules;
        return afterDiscount >= freeShippingThreshold ? 0 : shippingCost;
      },

      gst: () => {
        const subtotal = get().subtotal();
        const discount = get().discount();
        const shipping = get().shipping();
        // GST is included in the price (inclusive), so we calculate the GST component
        const taxableAmount = ((subtotal - discount + shipping) * GST_RATE) / (1 + GST_RATE);
        return Math.round(taxableAmount);
      },

      total: () => {
        const subtotal = get().subtotal();
        const discount = get().discount();
        const shipping = get().shipping();
        return subtotal - discount + shipping;
      },

      addItem: (product, variant, quantity = 1) => {
        set((state) => {
          const variantId = variant?.id;
          const existingIndex = state.items.findIndex(
            (item) => item.productId === product.id && (item.variant?.id || undefined) === variantId
          );

          const price = variant?.price || product.price;
          const comparePrice = variant?.compareAtPrice || product.compareAtPrice;
          // Stock lives on the variant as inventory.quantity; untracked or backorderable
          // products are only limited by checkout's per-line cap.
          const raw = product as any;
          const tracked = raw.track_inventory !== false && raw.trackInventory !== false && raw.allow_backorder !== true;
          const stock = (variant as any)?.inventory?.quantity ?? variant?.inventoryCount ?? raw.inventory_count ?? product.inventoryCount;
          const maxQuantity = tracked && typeof stock === 'number' ? Math.min(Math.max(stock, 0), MAX_LINE_QUANTITY) : MAX_LINE_QUANTITY;
          if (maxQuantity <= 0) {
            useUIStore.getState().showToast(`${product.name}${variant?.name ? ` (${variant.name})` : ''} is out of stock`, 'error');
            return state;
          }

          const newItem: CartItem = {
            productId: product.id,
            slug: product.slug,
            name: product.name,
            image: product.images[0]?.url || '',
            price,
            compareAtPrice: comparePrice,
            variant: variant
              ? { id: variant.id, name: variant.name, sku: variant.sku }
              : undefined,
            quantity,
            maxQuantity,
          };

          if (existingIndex >= 0) {
            const updatedItems = [...state.items];
            const existing = updatedItems[existingIndex];
            if (existing) {
              const newQuantity = Math.min(existing.quantity + quantity, maxQuantity);
              updatedItems[existingIndex] = { ...existing, quantity: newQuantity };
            }
            return { items: updatedItems };
          }

          return { items: [...state.items, newItem] };
        });

        useUIStore.getState().showToast(`${product.name} added to cart`, 'success', product.images[0]?.url || (product as any).images_json?.[0]?.url);
      },

      removeItem: (productId, variantId) => {
        set((state) => ({
          items: state.items.filter(
            (item) => !(item.productId === productId && (item.variant?.id || undefined) === variantId)
          ),
        }));
      },

      updateQuantity: (productId, variantId, quantity) => {
        set((state) => ({
          items: state.items.map((item) => {
            if (item.productId === productId && (item.variant?.id || undefined) === variantId) {
              return { ...item, quantity: Math.max(1, Math.min(quantity, item.maxQuantity)) };
            }
            return item;
          }),
        }));
      },

      incrementQuantity: (productId, variantId) => {
        set((state) => ({
          items: state.items.map((item) => {
            if (item.productId === productId && (item.variant?.id || undefined) === variantId) {
              const newQty = Math.min(item.quantity + 1, item.maxQuantity);
              return { ...item, quantity: newQty };
            }
            return item;
          }),
        }));
      },

      decrementQuantity: (productId, variantId) => {
        set((state) => ({
          items: state.items.map((item) => {
            if (item.productId === productId && (item.variant?.id || undefined) === variantId) {
              return { ...item, quantity: Math.max(1, item.quantity - 1) };
            }
            return item;
          }),
        }));
      },

      clearCart: () => {
        set({ items: [], couponCode: null, couponDiscount: 0 });
      },

      applyCoupon: (code, discount) => {
        set({ couponCode: code, couponDiscount: discount });
      },

      removeCoupon: () => {
        set({ couponCode: null, couponDiscount: 0 });
      },

      openCart: () => set({ isCartOpen: true }),
      closeCart: () => set({ isCartOpen: false }),
      toggleCart: () => set((state) => ({ isCartOpen: !state.isCartOpen })),
    }),
    {
      name: 'wonderherbals-cart',
      partialize: (state) => ({
        items: state.items,
        couponCode: state.couponCode,
        couponDiscount: state.couponDiscount,
      }),
    }
  )
);

/** Load the live shipping rules the admin configured (public read of admin_settings). */
supabase
  .from('admin_settings')
  .select('shipping')
  .eq('id', 'store_settings')
  .maybeSingle()
  .then(({ data }) => {
    const shipping = (data as any)?.shipping;
    if (!shipping) return;
    const toPaise = (v: unknown, fallback: number) =>
      v === undefined || v === null || v === '' || !Number.isFinite(Number(v)) ? fallback : Math.round(Number(v) * 100);
    useCartStore.setState({
      shippingRules: {
        freeShippingThreshold: toPaise(shipping.freeShippingMin, DEFAULT_SHIPPING_RULES.freeShippingThreshold),
        shippingCost: toPaise(shipping.standardShipping, DEFAULT_SHIPPING_RULES.shippingCost),
      },
    });
  }, () => undefined);
